// ============================================================================
// optimisticTick — pure projection helpers for the recurring canvas's
// tick-completion optimistic update.
// ----------------------------------------------------------------------------
// Extracted from ChecklistDrawingBoard.executeNodeCompletion so the tick
// projection is testable in isolation. The recurring branch persists
// completion state via `setOccurrenceCompletion` (which writes to the
// book page's `completions[id]`), and mirrors the write back onto the
// projected `ChecklistItem.occurrences[focusedKey]` entry so BoardNode's
// read (`node.occurrences?.[lens.focusedKey]?.isCompleted`) flips
// immediately — TimelineContext's book-model subscription only watches
// today's page, so on a non-today Daily / Weekly page the write would
// otherwise persist silently and the tap would appear to do nothing.
// ============================================================================

import type { ChecklistItem, ChecklistWorkflowStatus, RecurringOccurrenceState } from '../../types/schema'

/** Return `node` with `occurrences[focusedKey]` patched to reflect the
 *  new completion + workflow status. Non-recurring nodes are returned
 *  unchanged so the caller can call this unconditionally in a map. */
export const projectOptimisticRecurringTick = (
  node: ChecklistItem,
  focusedKey: string,
  isCompleted: boolean,
): ChecklistItem => {
  const workflowStatus: ChecklistWorkflowStatus = isCompleted ? 'COMPLETED' : 'ONGOING'
  const previousEntry: Partial<RecurringOccurrenceState> = node.occurrences?.[focusedKey] ?? {}
  const nextOccurrences: Record<string, RecurringOccurrenceState> = {
    ...(node.occurrences ?? {}),
    [focusedKey]: {
      ...previousEntry,
      isCompleted,
      workflowStatus,
    } as RecurringOccurrenceState,
  }
  return { ...node, occurrences: nextOccurrences }
}

/** Roll back the optimistic update by restoring the pre-tick occurrences
 *  map. Used from the catch branch when persistence rejects. */
export const revertOptimisticRecurringTick = (
  node: ChecklistItem,
  previousOccurrences: ChecklistItem['occurrences'],
): ChecklistItem => ({
  ...node,
  occurrences: previousOccurrences,
})
